/**
 * UTM tracking — first-touch attribution for campaign ROI.
 *
 * Flow:
 *   1. On every page load, `storeFirstTouchUtms()` captures ?utm_* params
 *      from the URL + referrer.
 *   2. First-touch (the FIRST campaign that brought the user) is sticky
 *      in localStorage for the lifetime of the browser profile — never
 *      overwritten. This is what "true" attribution measures.
 *   3. Last-touch (the campaign that drove the most recent visit) is
 *      overwritten on every visit. Useful for "what tipped them over"
 *      analysis.
 *   4. When a user signs up, `getStoredUtms()` is read and patched into
 *      the `public.users` row so the admin Signups dashboard shows
 *      source per signup.
 *
 * Why localStorage (not sessionStorage):
 *   Influencer flow is often: see reel → click link days later → maybe
 *   sign up next week. localStorage persists across sessions, captures
 *   the original touchpoint.
 *
 * Privacy: we capture campaign UTMs + referrer hostname only. No user
 * activity, no fingerprinting. UTMs are opt-in via the URL — they're
 * literally what the influencer/campaign appended to the link.
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const FIRST_TOUCH_KEY = 'eager_first_touch_utms';
const LAST_TOUCH_KEY  = 'eager_last_touch_utms';

/** Parse the current URL for UTM params + capture referrer hostname. */
function readFromUrlAndReferrer() {
    if (typeof window === 'undefined') return null;
    try {
        const url = new URL(window.location.href);
        const utms = {};
        UTM_KEYS.forEach((k) => {
            const v = url.searchParams.get(k);
            if (v) utms[k] = v.slice(0, 200); // defensive truncation
        });

        // Referrer is useful even WITHOUT UTM params — it tells us they
        // came from Instagram / WhatsApp / a specific blog, etc.
        let referrerHost = null;
        if (document.referrer) {
            try { referrerHost = new URL(document.referrer).hostname.replace(/^www\./, ''); }
            catch { /* malformed */ }
        }

        // Skip noise: if there are no UTMs AND referrer is the same origin
        // (in-app navigation), don't record anything.
        const sameOrigin = referrerHost && (referrerHost === window.location.hostname.replace(/^www\./, ''));
        if (Object.keys(utms).length === 0 && (!referrerHost || sameOrigin)) return null;

        return {
            ...utms,
            referrer: referrerHost,
            landing_page: url.pathname,
            captured_at: new Date().toISOString(),
        };
    } catch {
        return null;
    }
}

/** Persist first-touch (sticky) + last-touch (rolling). Call on every page load. */
export function storeFirstTouchUtms() {
    if (typeof window === 'undefined') return null;
    const captured = readFromUrlAndReferrer();
    if (!captured) return null;
    try {
        if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
            localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(captured));
        }
        localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(captured));
    } catch { /* private mode */ }
    return captured;
}

/** Returns { first, last } UTM dicts. Either can be null. */
export function getStoredUtms() {
    if (typeof window === 'undefined') return { first: null, last: null };
    try {
        const first = JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || 'null');
        const last  = JSON.parse(localStorage.getItem(LAST_TOUCH_KEY)  || 'null');
        return { first, last };
    } catch {
        return { first: null, last: null };
    }
}

/**
 * Build the column-shaped payload that maps to the `users` table columns
 * added by `admin-utm-tracking-setup.sql`. Returns null if there's
 * nothing useful to write.
 */
export function buildUtmProfilePatch() {
    const { first } = getStoredUtms();
    if (!first) return null;
    const patch = {};
    if (first.utm_source)    patch.utm_source    = first.utm_source;
    if (first.utm_medium)    patch.utm_medium    = first.utm_medium;
    if (first.utm_campaign)  patch.utm_campaign  = first.utm_campaign;
    if (first.utm_content)   patch.utm_content   = first.utm_content;
    if (first.utm_term)      patch.utm_term      = first.utm_term;
    if (first.referrer)      patch.utm_referrer  = first.referrer;
    if (first.landing_page)  patch.utm_landing_page = first.landing_page;
    if (first.captured_at)   patch.utm_first_touch_at = first.captured_at;
    return Object.keys(patch).length ? patch : null;
}
